import { useRef } from "react";
import { motion, useInView } from "framer-motion";
import { Phone, MessageCircle, MapPin, Clock, Mail } from "lucide-react";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";

const PhoneButton = ({ className, children }) => (
  <DropdownMenu>
    <DropdownMenuTrigger asChild>
      <button className={className}>{children}</button>
    </DropdownMenuTrigger>
    <DropdownMenuContent className="bg-white">
      <DropdownMenuItem asChild>
        <a href="tel:0608691129" className="flex items-center gap-2 cursor-pointer">
          <Phone className="w-4 h-4" /> Appeler
        </a>
      </DropdownMenuItem>
      <DropdownMenuItem asChild>
        <a href="sms:0608691129" className="flex items-center gap-2 cursor-pointer">
          <MessageCircle className="w-4 h-4" /> Envoyer un SMS
        </a>
      </DropdownMenuItem>
    </DropdownMenuContent>
  </DropdownMenu>
);

const ContactSection = () => {
  const ref = useRef(null);
  const isInView = useInView(ref, { once: true, margin: "-100px" });

  return (
    <section
      id="contact"
      ref={ref}
      className="py-24 bg-stone-100"
    >
      <div className="max-w-5xl mx-auto px-6">
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          animate={isInView ? { opacity: 1, y: 0 } : {}}
          transition={{ duration: 0.6 }}
          className="text-center mb-12"
        >
          <h2 className="font-serif text-4xl md:text-5xl text-[#4A3728]">
            Contactez-nous
          </h2>
        </motion.div>

        <div className="grid md:grid-cols-2 gap-8 items-stretch">
          {/* Carte téléphone + infos */}
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            animate={isInView ? { opacity: 1, y: 0 } : {}}
            transition={{ duration: 0.6, delay: 0.2 }}
            className="bg-[#4A3728] text-white p-8 rounded-lg flex flex-col justify-between"
          >
            <div>
              <h3 className="font-serif text-2xl mb-6">Nous contacter</h3>
              <div className="space-y-5">
                <div className="flex items-center gap-4">
                  <div className="w-10 h-10 bg-white/10 rounded-full flex items-center justify-center flex-shrink-0">
                    <Phone className="w-5 h-5 text-[#D4A373]" />
                  </div>
                  <PhoneButton className="text-white hover:text-[#D4A373] font-medium text-lg">
                    06 08 69 11 29
                  </PhoneButton>
                </div>
                <div className="flex items-center gap-4">
                  <div className="w-10 h-10 bg-white/10 rounded-full flex items-center justify-center flex-shrink-0">
                    <Mail className="w-5 h-5 text-[#D4A373]" />
                  </div>
                  <span className="text-white/85 select-text">
                    aubondebarras1@gmail.com
                  </span>
                </div>
                <div className="flex items-start gap-4">
                  <div className="w-10 h-10 bg-white/10 rounded-full flex items-center justify-center flex-shrink-0">
                    <MapPin className="w-5 h-5 text-[#D4A373]" />
                  </div>
                  <a
                    href="https://www.google.com/maps/search/?api=1&query=102+Av.+du+Passous+50230+Agon-Coutainville"
                    target="_blank"
                    rel="noopener noreferrer"
                    className="text-white/80 hover:text-[#D4A373] transition-colors"
                  >
                    102 Av. du Passous,<br />50230 Agon-Coutainville
                  </a>
                </div>
                <div className="flex items-start gap-4">
                  <div className="w-10 h-10 bg-white/10 rounded-full flex items-center justify-center flex-shrink-0">
                    <Clock className="w-5 h-5 text-[#D4A373]" />
                  </div>
                  <span className="text-white/80">
                    Du vendredi au dimanche<br />de 14h à 19h
                  </span>
                </div>
              </div>
            </div>
          </motion.div>

          {/* Carte Google Maps */}
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            animate={isInView ? { opacity: 1, y: 0 } : {}}
            transition={{ duration: 0.6, delay: 0.4 }}
            className="rounded-lg overflow-hidden shadow-lg min-h-[320px]"
          >
            <iframe
              src="https://www.google.com/maps/embed?pb=!1m18!1m12!1m3!1d2611.8!2d-1.5997!3d49.0489!2m3!1f0!2f0!3f0!3m2!1i1024!2i768!4f13.1!3m3!1m2!1s0x480baf82c0ae9a6b%3A0x0!2s102%20Av.%20du%20Passous%2C%2050230%20Agon-Coutainville!5e0!3m2!1sfr!2sfr!4v1700000000000!5m2!1sfr!2sfr"
              width="100%"
              height="100%"
              style={{ border: 0, minHeight: "320px" }}
              allowFullScreen=""
              loading="lazy"
              title="Localisation Au Bon Débarras"
            />
          </motion.div>
        </div>
      </div>

      {/* Footer */}
      <div className="mt-24 bg-[#4A3728] text-white py-8">
        <div className="max-w-7xl mx-auto px-6 text-center">
          <p className="text-white/60 text-sm">
            © {new Date().getFullYear()} Au Bon Débarras. Tous droits réservés.
          </p>
        </div>
      </div>
    </section>
  );
};

export default ContactSection;
